import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import upperFirst from "lodash/upperFirst";
import { useEffect, useRef, useState } from "react";

const fonts = [
  "Pacifico",
  "Roboto",
  "Cursive",
  "Lobster",
  "Dancing Script",
  "Great Vibes",
];

const titleCase = (str: string) => {
  return str.toLowerCase().split(" ").map(upperFirst).join(" ");
};

type TypedSignatureProps = {
  onSave: (data: { type: string; dataURL: string }) => void;
  fontsSelecatbale: boolean;
};

const TypedSignature = ({ onSave, fontsSelecatbale }: TypedSignatureProps) => {
  const [name, setName] = useState("");
  const [selectedFont, setSelectedFont] = useState(fonts[0]);

  // Dynamically load Google Fonts
  useEffect(() => {
    const link = document.createElement("link");
    link.href = `https://fonts.googleapis.com/css2?${fonts
      .map((font) => `family=${font.replace(/ /g, "+")}`)
      .join("&")}&display=swap`;
    link.rel = "stylesheet";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const saveAsPng = () => {
    const canvas = canvasRef.current;

    if (!canvas) {
      console.error("Canvas element is not available.");
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("Canvas rendering context is not available.");
      return;
    }

    // Temporary canvas size for drawing text
    const tempWidth = 800;
    const tempHeight = 200;
    canvas.width = tempWidth;
    canvas.height = tempHeight;

    // Clear the canvas (ensures transparency)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply styles and draw text
    const fontSize = 120; // Font size in pixels
    ctx.font = `${fontSize}px ${selectedFont}`;
    ctx.fillStyle = "#000"; // Black color for the text
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const text = name || "Your Signature";
    ctx.fillText(text, tempWidth / 2, tempHeight / 2);

    // Measure text dimensions
    const textMetrics = ctx.measureText(text);
    const textWidth = Math.ceil(textMetrics.width);
    const ascent = Math.ceil(textMetrics.actualBoundingBoxAscent);
    const descent = Math.ceil(textMetrics.actualBoundingBoxDescent);
    const textHeight = ascent + descent;

    // Add padding
    const padding = 20;

    // Create a temporary canvas to trim exact content bounds
    const trimmedCanvas = document.createElement("canvas");
    trimmedCanvas.width = textWidth + padding * 2;
    trimmedCanvas.height = textHeight + padding * 2;
    const trimmedCtx = trimmedCanvas.getContext("2d");

    if (!trimmedCtx) {
      console.error("Trimmed canvas rendering context is not available.");
      return;
    }

    // Draw text onto the trimmed canvas
    trimmedCtx.font = `${fontSize}px ${selectedFont}`;
    trimmedCtx.fillStyle = "#000";
    trimmedCtx.textAlign = "center";
    trimmedCtx.textBaseline = "middle";
    trimmedCtx.clearRect(0, 0, trimmedCanvas.width, trimmedCanvas.height);
    trimmedCtx.fillText(
      text,
      trimmedCanvas.width / 2,
      padding + ascent, // Align text properly with padding
    );

    // Export canvas content as PNG
    const dataURL = trimmedCanvas.toDataURL("image/png");
    onSave({ type: "typed", dataURL }); // Pass the PNG data URL to parent
  };

  return (
    <Box>
      {/* Signature Preview */}
      <Typography
        variant="h5"
        sx={{
          fontFamily: selectedFont,
          textAlign: "center",
          border: "1px dashed #ccc",
          padding: "16px",
          minHeight: "50px",
          margin: "16px auto",
          backgroundColor: "white",
        }}
      >
        {name || "Your Signature"}
      </Typography>

      {/* Hidden Canvas */}
      <canvas
        ref={canvasRef}
        width="400"
        height="100"
        style={{ display: "none" }}
      />

      {/* Input Field */}
      <Box
        display="flex"
        mb={1}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <TextField
          sx={{
            mt: 2,
            maxWidth: 300,
            backgroundColor: "white",
            "& .MuiInputBase-input": {
              textAlign: "center", // Align text to the center
            },
          }}
          fullWidth
          label="Enter Full Name"
          variant="outlined"
          value={name}
          size="small"
          onChange={(e) => setName(titleCase(e.target.value))}
        />

        <Typography
          variant="caption"
          sx={{ textAlign: "center", color: "#105998" }}
        >
          Your full name as it appears on official documents.
        </Typography>

        {/* Font Selector */}
        {fontsSelecatbale && (
          <FormControl fullWidth size="small">
            <InputLabel id="font-selector-label">Select Font</InputLabel>
            <Select
              labelId="font-selector-label"
              value={selectedFont}
              onChange={(e) => setSelectedFont(e.target.value)}
              label="Select Font"
            >
              {fonts.map((font) => (
                <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                  {font}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Save Button */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          size="medium"
          sx={{ textTransform: "none", mb: 1 }}
          onClick={saveAsPng}
          disabled={name.trim().length < 5}
        >
          Sign and Continue
        </Button>
      </Box>
    </Box>
  );
};

export default TypedSignature;
